import React, { useEffect, useState} from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import { getUrl } from "src/Utils/Common";
import CustomSelectBox from "src/Components/shared/shared/CustomSelectBox";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import ProgressBar from "src/Components/shared/shared/ProgressBar";
import useRefreshData from "src/Utils/CustomHooks/useRefreshData";
import { theme } from "../../../Theme";
import { useTheme } from "@mui/material/styles";


function transformToFlatArray(hier, market, manufacturers) {
  const result = [];
  console.log("[MarketKPIsTable] transformToFlatArray", {
    hier,
    marketKeys: market && typeof market === "object" ? Object.keys(market) : null,
    manufacturersKeys:
      manufacturers && typeof manufacturers === "object" ? Object.keys(manufacturers) : null,
  });

  const resolveHierarchyKey = (requestedKey, obj) => {
    if (!obj || typeof obj !== "object") return requestedKey;
    if (Array.isArray(obj[requestedKey])) return requestedKey;

    // Common backend/excel aliases we use across the app
    const candidates =
      requestedKey === "class" || requestedKey === "Class" || requestedKey === "segment" || requestedKey === "segmnt"
        ? ["segmnt", "segment", "class", "Class"]
        : requestedKey === "sku_db" || requestedKey === "sku_id"
          ? ["sku_id", "sku_db"]
          : [requestedKey];

    return candidates.find((k) => Array.isArray(obj[k])) || requestedKey;
  };

  const marketHierKey = resolveHierarchyKey(hier, market);
  const mfcHierKey = resolveHierarchyKey(hier, manufacturers);

  const marketCategories = Array.isArray(market?.[marketHierKey]) ? market[marketHierKey] : [];
  const mfcCategories = Array.isArray(manufacturers?.[mfcHierKey]) ? manufacturers[mfcHierKey] : [];

  for (let i = 0; i < marketCategories.length; i++) {
    const category = marketCategories[i];
    const mfcIndex = mfcCategories.indexOf(category);

    const safeRound = (val) => (Number.isFinite(Number(val)) ? Math.round(Number(val)) : 0);

    result.push({
      category,
      valueSales: safeRound(market?.value_sales?.[i]),
      mfcValueSales: mfcIndex >= 0 ? safeRound(manufacturers?.value_sales?.[mfcIndex]) : 0,
      wob: market?.Value_Share?.[i] ?? 0,
      marketValueSalesIYA: formatPercent(market?.Value_Sales_Growth?.[i]),
      volumeIYA: formatPercent(market?.Volume_Sales_Growth?.[i]),
      avgPriceVol: formatPrice(market?.price_index?.[i]),
      avgPriceVolIYA: formatPercent(market?.Price_Index_Growth?.[i]),
      mfcWob: mfcIndex >= 0 ? (manufacturers?.Value_Share?.[mfcIndex] ?? 0) : 0,
      mfcMarketShare: mfcIndex >= 0 ? manufacturers?.market_share?.[mfcIndex] : 0,
      mfcValueSalesIYA:
        mfcIndex >= 0 ? formatPercent(manufacturers?.Value_Sales_Growth?.[mfcIndex]) : "N/A",
      mfcVolumeIYA:
        mfcIndex >= 0 ? formatPercent(manufacturers?.Volume_Sales_Growth?.[mfcIndex]) : "N/A",
      mfcAvgPriceVol:
        mfcIndex >= 0 ? formatPrice(manufacturers?.price_index?.[mfcIndex]) : "N/A",
      mfcAvgPriceVolIYA:
        mfcIndex >= 0 ? formatPercent(manufacturers?.Price_Index_Growth?.[mfcIndex]) : "N/A",
      // Raw values for color determination
      rawMarketValueSalesIYA: market?.Value_Sales_Growth?.[i],
      rawVolumeIYA: market?.Volume_Sales_Growth?.[i],
      rawAvgPriceVol: market?.price_index?.[i],
      rawAvgPriceVolIYA: market?.Price_Index_Growth?.[i],
      rawMfcValueSalesIYA:
        mfcIndex >= 0 ? manufacturers?.Value_Sales_Growth?.[mfcIndex] : null,
      rawMfcVolumeIYA:
        mfcIndex >= 0 ? manufacturers?.Volume_Sales_Growth?.[mfcIndex] : null,
      rawMfcAvgPriceVol:
        mfcIndex >= 0 ? manufacturers?.price_index?.[mfcIndex] : null,
      rawMfcAvgPriceVolIYA:
        mfcIndex >= 0 ? manufacturers?.Price_Index_Growth?.[mfcIndex] : null,
    });
  }

  return result;
}

function formatPercent(value) {
  return Math.round(value).toString();
}

function formatPrice(value) {
  return value.toFixed(2);
}

function getValueColor(value) {
  if (value === null || value === undefined || value === "N/A") {
    return "inherit";
  }
  return value >= 100 ? "#2e7d32" : "#d32f2f";
}

function getComparisonBackground(row, mfcField, marketField) {
  if (
    row[mfcField] !== null &&
    row[marketField] !== null &&
    row[mfcField] < row[marketField]
  ) {
    return "#ffebee"; 
  }
  return "transparent";
}



function ManufacturerSelector({
  selectedManufacturer,
  setSelectedManufacturer,
  manufacturerOptions,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (value) => {
    if (value) setSelectedManufacturer(value);
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={{ xs: 0.35, sm: 0.5 }}
        onClick={handleClick}
        sx={{ cursor: "pointer" }}
      >
        <Typography
          variant="body2"
          sx={{ 
            color: "#1e221f", 
            fontWeight: "bold",
            fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
          }}
        >
          {selectedManufacturer}
        </Typography>
        <ArrowDropDownIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" } }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {manufacturerOptions.map((option) => (
          <MenuItem
            key={option}
            selected={option === selectedManufacturer}
            onClick={() => handleClose(option)}
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

const MarketKPIsTable = ({
  selectedHierarchy: propSelectedHierarchy,
  setSelectedHierarchy: propSetSelectedHierarchy,
  onDataLoaded,
}) => {
  const muiTheme = useTheme();
  const isSmallDown = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isMediumDown = useMediaQuery(muiTheme.breakpoints.down("md"));
  const brandColumnWidth = isSmallDown ? 90 : isMediumDown ? 150 : 120;

  const projectId = useSelector((state) => state.userProject.projectId);
  const [hierarchyOptions, setHierarchyOptions] = useState([]);

  const [localSelectedHierarchy, setLocalSelectedHierarchy] = useState(
    propSelectedHierarchy || "class"
  );
  const selectedHierarchy =
    propSelectedHierarchy !== undefined
      ? propSelectedHierarchy
      : localSelectedHierarchy;

  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState("");

  const [expandedRows, setExpandedRows] = useState(new Set());
  const [nestedData, setNestedData] = useState({});
  const [loadingNested, setLoadingNested] = useState(false);
  const [expandedNestedRows, setExpandedNestedRows] = useState(new Set());
  const [nestedNestedData, setNestedNestedData] = useState({});
  const [availableHierarchyKeys, setAvailableHierarchyKeys] = useState([]);
  const isFreeTrialUser = localStorage.getItem("role") === "free_trial";

  const segmentHierarchyKey = React.useMemo(() => {
    if (availableHierarchyKeys.includes("segmnt")) return "segmnt";
    if (availableHierarchyKeys.includes("segment")) return "segment";
    return "class";
  }, [availableHierarchyKeys]);

  const mapHierarchyForApi = React.useCallback(
    (hier) => {
      if (!isFreeTrialUser) return hier;
      if (hier === "sku_db") return "sku_id";
      if (hier === "Class" || hier === "class" || hier === "segment" || hier === "segmnt") {
        return segmentHierarchyKey;
      }
      return hier;
    },
    [isFreeTrialUser, segmentHierarchyKey]
  );
  const [loadingSKU, setLoadingSKU] = useState(false);
  
  const getDefaultManufacturer = (manufacturerOptions) => {
    if (!manufacturerOptions || manufacturerOptions.length === 0) {
      console.log("[MarketKPIsTable] No manufacturer options available");
      return "";
    }
        
    if (projectId === 6) {
      const savolaGroup = manufacturerOptions.find(option => 
        option.toLowerCase().includes("savola") || 
        option.toLowerCase().includes("savola group") ||
        option.toLowerCase() === "savola group"
      );
      const selected = savolaGroup || manufacturerOptions[0];
      return selected;
    }
    
    const selected = manufacturerOptions[0];
    return selected;
  };
  
  const handleHierarchyChange = (newHierarchy) => {
    console.log(
      "[MarketKPIsTable] handleHierarchyChange called with:",
      newHierarchy
    );
    console.log(
      "[MarketKPIsTable] propSetSelectedHierarchy exists:",
      !!propSetSelectedHierarchy
    );
    console.log(
      "[MarketKPIsTable] propSetSelectedHierarchy type:",
      typeof propSetSelectedHierarchy
    );

    if (propSetSelectedHierarchy && typeof propSetSelectedHierarchy === 'function') {
      propSetSelectedHierarchy(newHierarchy);
    } else {
      setLocalSelectedHierarchy(newHierarchy);
    }
  };
  const [loadingHierarchy, setLoadingHierarchy] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [error, setError] = useState(null);
  const loading = loadingHierarchy || loadingTable;

  const [marketData, setMarketData] = useState([]);
  const maxMfcValue = Math.max(...marketData.map((row) => row.mfcValueSales));

  const maxValue = Math.max(...marketData.map((row) => row.valueSales));

  const resetTableData = () => {
    setMarketData([]);
    setNestedData({});
    setNestedNestedData({});
    setExpandedRows(new Set());
    setExpandedNestedRows(new Set());
  };

  const resetTableFilters = () => {
    setSelectedManufacturer(getDefaultManufacturer(manufacturerOptions));
    if (propSetSelectedHierarchy) {
      propSetSelectedHierarchy("class");
    } else {
      setLocalSelectedHierarchy("class");
    }
  };

  const resetTableError = () => {
    setError(null);
  };

  const resetTableLoading = () => {
    setLoadingHierarchy(false);
    setLoadingTable(false);
  };

  const { handleRefresh, isRefreshing } = useRefreshData(
    () => {
      setLoadingHierarchy(true);
      setLoadingTable(true);
    },
    [resetTableData, resetTableFilters, resetTableError, resetTableLoading]
  );

  const handleRowToggle = (categoryName, rowIndex) => {
    const newExpandedRows = new Set(expandedRows); 
    if (expandedRows.has(rowIndex)) {
      newExpandedRows.delete(rowIndex);
    } else {
      newExpandedRows.add(rowIndex);
      
      if (!nestedData[categoryName]) {
        const targetHierarchy =
          selectedHierarchy === "brand" ? mapHierarchyForApi("class") : "brand";
        console.log("[MarketKPIsTable] will fetch nested rows", {
          categoryName,
          selectedHierarchy,
          targetHierarchy,
        });
        fetchNestedTableData(targetHierarchy, categoryName);
      }
    }
    setExpandedRows(newExpandedRows);
  };

  useEffect(() => {
    if (propSelectedHierarchy !== undefined && propSelectedHierarchy !== localSelectedHierarchy) {
      setLocalSelectedHierarchy(propSelectedHierarchy);
    }
  }, [propSelectedHierarchy, localSelectedHierarchy]);

  useEffect(() => {
    function fetchHierarchyLevels() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setError("Project ID not available");
        setLoadingHierarchy(false);
        return;
      }
      setLoadingHierarchy(true);
      setError(null);
      fetch(`${getUrl()}/market_overview/hierarchy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify({ project_id: projectId }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          console.log("result from hierarcy", result);

          const hierarchyData =
            result?.["0"] ||
            result?.Product ||
            result?.product ||
            {};

          if (Object.keys(hierarchyData).length === 0) {
            throw new Error("No hierarchy data available");
          }
          
          const allowedHierarchyLevels = ["brand", "class", "segmnt", "segment"];
          const rawHierarchyLevels = Object.keys(hierarchyData);
          setAvailableHierarchyKeys(rawHierarchyLevels);
          const hierarchyLevels = rawHierarchyLevels.filter((key) =>
            allowedHierarchyLevels.includes(key)
          );
          hierarchyLevels.push("channel", "retailer");
          setHierarchyOptions(hierarchyLevels);

          const manufacturers = hierarchyData.manufacturer || [];
          setManufacturerOptions(manufacturers);

          if (!propSelectedHierarchy) {
            console.log(
              "[MarketKPIsTable] No propSelectedHierarchy provided, setting default to:",
              hierarchyLevels[0]
            );
            setLocalSelectedHierarchy(
              hierarchyLevels.includes("brand") ? "brand" : hierarchyLevels[0] || ""
            );
          } else {
            console.log(
              "[MarketKPIsTable] Using propSelectedHierarchy:",
              propSelectedHierarchy
            );
          }
          
          setError(null);
        })
        .catch((error) => {
          console.error("Error fetching hierarchy levels:", error);
          setError(error.message || "Failed to fetch hierarchy levels");
        })
        .finally(() => {
          setLoadingHierarchy(false);
        });
    }

    fetchHierarchyLevels();
  }, [projectId]);

  useEffect(() => {
    if (manufacturerOptions.length > 0 && !selectedManufacturer) {
      const defaultManufacturer = getDefaultManufacturer(manufacturerOptions);
      setSelectedManufacturer(defaultManufacturer);
    }
  }, [manufacturerOptions, projectId]); 

  useEffect(() => {
    function fetchTableData() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setError("ProjectID not available");
        setLoadingTable(false);
        return;
      }

      if (!selectedManufacturer) {
        console.log("[MarketKPIsTable] No manufacturer selected yet, skipping data fetch");
        setLoadingTable(false);
        return;
      }

      setLoadingTable(true);
      setError(null);
      fetch(
        `${getUrl()}/market_overview/segments_market_manufacturer?project_id=${projectId}&filters={}&comparison_type=years&hierarchy=${selectedHierarchy}&target=1&manufacturer=${selectedManufacturer}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": localStorage.getItem("userId") || "",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.text();
        })
        .then((text) => {
          try {
            const cleaned = text.replace(/\bNaN\b/g, "null");
            const result = JSON.parse(cleaned);
            console.log(result);
            
            if (!result.Market || !result.Manufacturers) {
              throw new Error("Invalid data structure received from API");
            }
            
            const transformed = transformToFlatArray(
              selectedHierarchy,
              result.Market,
              result.Manufacturers
            );
            
            if (!transformed || transformed.length === 0) {
              throw new Error("No data available for selected filters");
            }
            
            setMarketData(transformed);
            setError(null);
            if (onDataLoaded) onDataLoaded(transformed);
          } catch (err) {
            console.error("Error parsing cleaned JSON:", err);
            setError(err.message || "Error processing data");
          }
        })

        .catch((error) => {
          console.error("Error fetching market comparison data:", error);
          setError(error.message || "Failed to fetch data");
        })
        .finally(() => {
          setLoadingTable(false);
        });
    }

    fetchTableData();
  }, [selectedHierarchy, selectedManufacturer]);

  function fetchNestedTableData(targetHierarchy, categoryFilter) {
    if (!projectId) {
      console.warn("Project ID is not set in Redux state.");
      return;
    }

    setLoadingNested(true);
    const effectiveSelectedHierarchy = mapHierarchyForApi(selectedHierarchy);
    const effectiveTargetHierarchy = mapHierarchyForApi(targetHierarchy);
    const nestedUrl = `${getUrl()}/market_overview/segments_market_manufacturer?project_id=${projectId}&filters={"${effectiveSelectedHierarchy}":"${categoryFilter}"}&comparison_type=years&hierarchy=${effectiveTargetHierarchy}&target=1&manufacturer=${selectedManufacturer}`;
    console.log("[MarketKPIsTable] nested request", nestedUrl);

    // Debug log for copying into Postman (Nested table)
    console.groupCollapsed("[MarketKPIsTable] nested table request");
    console.log("GET", nestedUrl);
    console.log("headers", {
      "Content-Type": "application/json",
      "x-access-token": localStorage.getItem("userId") || "",
      "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
    });
    console.log("query", {
      project_id: projectId,
      filters: { [effectiveSelectedHierarchy]: categoryFilter },
      comparison_type: "years",
      hierarchy: effectiveTargetHierarchy,
      target: 1,
      manufacturer: selectedManufacturer,
    });
    console.groupEnd();
    fetch(
      nestedUrl,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        try {
          const cleaned = text.replace(/\bNaN\b/g, "null");
          const result = JSON.parse(cleaned);
          console.log("Nested result:", result);
          const transformed = transformToFlatArray(
            targetHierarchy,
            result.Market,
            result.Manufacturers
          );
          
          setNestedData(prev => ({
            ...prev,
            [categoryFilter]: transformed
          }));
        } catch (err) {
          console.error("Error parsing nested JSON:", err);
        }
      })
      .catch((error) => {
        console.error("Error fetching nested market comparison data:", error);
      })
      .finally(() => {
        setLoadingNested(false);
      });
  }

  useEffect(() => {
    if (
      propSelectedHierarchy &&
      propSelectedHierarchy !== localSelectedHierarchy
    ) {
      setLocalSelectedHierarchy(propSelectedHierarchy);
    }
  }, [propSelectedHierarchy]);

  useEffect(() => {
    console.log(
      "[MarketKPIsTable] selectedHierarchy changed:",
      selectedHierarchy
    );
    setExpandedRows(new Set());
    setNestedData({});
  }, [selectedHierarchy]);

  const handleNestedRowToggle = (parentCategory, categoryName, rowIndex) => {
    const key = `${parentCategory}-${rowIndex}`;
    const newExpandedNestedRows = new Set(expandedNestedRows);
  
    if (expandedNestedRows.has(key)) {
      newExpandedNestedRows.delete(key);
    } else {
      newExpandedNestedRows.add(key);
        if (!nestedNestedData[key]) {
          const oppositeType =
            selectedHierarchy === "brand" ? mapHierarchyForApi("class") : "brand";
          fetchSKUData(oppositeType, categoryName, key, parentCategory);
        }
    }
    setExpandedNestedRows(newExpandedNestedRows);
  };
  
function fetchSKUData(rowType, rowName, key, parentCategory) {
  const targetHierarchy = mapHierarchyForApi("sku_db");
  let filtersObj;
  const effectiveRowType = mapHierarchyForApi(rowType);
  if (effectiveRowType === segmentHierarchyKey) {
    filtersObj = { [segmentHierarchyKey]: rowName, brand: parentCategory };
  } else {
    filtersObj = { brand: rowName, [segmentHierarchyKey]: parentCategory };
  }
  const filtersJSON = encodeURIComponent(JSON.stringify(filtersObj));
  const url = `${getUrl()}/market_overview/segments_market_manufacturer?project_id=${projectId}&filters=${filtersJSON}&comparison_type=years&hierarchy=${targetHierarchy}&target=1&manufacturer=${selectedManufacturer}`;
  setLoadingSKU(true);
  console.log("[MarketKPIsTable] SKU nested request", url);

  // Debug log for copying into Postman (SKU nested-nested)
  console.groupCollapsed("[MarketKPIsTable] SKU nested request");
  console.log("GET", url);
  console.log("headers", {
    "Content-Type": "application/json",
    "x-access-token": localStorage.getItem("userId") || "",
    "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
  });
  console.log("query", {
    project_id: projectId,
    filters: filtersObj,
    comparison_type: "years",
    hierarchy: targetHierarchy,
    target: 1,
    manufacturer: selectedManufacturer,
  });
  console.groupEnd();

  fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "x-access-token": localStorage.getItem("userId") || "",
      "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
    },
  })
    .then((res) => res.text())
    .then((text) => {
      const cleaned = text.replace(/\bNaN\b/g, "null");
      if (!cleaned.trim().startsWith("{") && !cleaned.trim().startsWith("[")) {
        console.error("API did not return JSON:", cleaned);
        return;
      }
      const result = JSON.parse(cleaned);
      const transformed = transformToFlatArray(
        targetHierarchy,
        result.Market,
        result.Manufacturers
      );
      setNestedNestedData((prev) => ({
        ...prev,
        [key]: transformed,
      }));
    })
    .catch((err) => {
      console.error("fetchSKUData error:", err);
    })
    .finally(() => {
      setLoadingSKU(false);
    });
}

  const renderTableRow = (row, idx, isNested = false, parentCategory = null, isSKU = false) => {
    const isTotalRow = String(row?.category ?? "").trim().toLowerCase() === "total";
    const maxValueForRow = isNested ? 
      Math.max(...(nestedData[parentCategory] || []).map((r) => r.valueSales)) : 
      maxValue;
    const maxMfcValueForRow = isNested ? 
      Math.max(...(nestedData[parentCategory] || []).map((r) => r.mfcValueSales)) : 
      maxMfcValue;

    const baseRowBg = isSKU ? "#dadada" : isNested ? "#f0f0f0" : "white";
    return (
      <TableRow 
        key={isNested ? `nested-${parentCategory}-${idx}` : idx}
        sx={{
          backgroundColor: isTotalRow ? "#fdf5db" : baseRowBg,
          borderBottom: "1px solid #f0f0f0",
          "& td": isTotalRow
            ? {
                fontWeight: 700,
              }
            : undefined,
          "&:hover": isTotalRow
            ? {
                backgroundColor: "#fff3cd",
              }
            : undefined,
        }}
      >
        <TableCell
          align="left"
          sx={{
            fontWeight: isTotalRow ? 700 : "medium",
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            minWidth: brandColumnWidth,
            maxWidth: brandColumnWidth + 40,
            width: brandColumnWidth,
            borderRight: "1px solid #e0e0e0",
            cursor:
              isTotalRow
                ? "default"
                :
              !isNested &&
              (selectedHierarchy === "brand" ||
                selectedHierarchy === "class" ||
                selectedHierarchy === "segmnt" ||
                selectedHierarchy === "segment")
                ? "pointer"
                : "default",
            paddingLeft: isSKU ? { xs: 6, sm: 7, md: 8 } : isNested ? { xs: 3, sm: 3.5, md: 4 } : { xs: 0.5, sm: 0.75, md: 1 },
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
          onClick={
            isTotalRow
              ? undefined
              :
            !isNested &&
            (selectedHierarchy === "brand" ||
              selectedHierarchy === "class" ||
              selectedHierarchy === "segmnt" ||
              selectedHierarchy === "segment")
              ? 
            () => handleRowToggle(row.category, idx) : undefined}
        >
          <Box display="flex" alignItems="center" gap={0.5} width="100%" minWidth={0}>
            {!isNested ? (
              row.category !== "Total" &&
              (selectedHierarchy === "brand" ||
                selectedHierarchy === "class" ||
                selectedHierarchy === "segmnt" ||
                selectedHierarchy === "segment") ? (
                <IconButton
                  size="small"
                  onClick={() => handleRowToggle(row.category, idx)}
                  sx={{ 
                    flexShrink: 0,
                    padding: { xs: 0.25, sm: 0.4, md: 0.5 },
                    color: "#1976d2",
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                      color: "#1565c0",
                      transform: "scale(1.1)"
                    },
                    transition: "all 0.2s ease-in-out"
                  }}
                >
                  {expandedRows.has(idx) ? (
                    <ExpandLess sx={{ fontSize: { xs: "0.9rem", sm: "1rem", md: "1.2rem" }, fontWeight: "bold" }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: { xs: "0.9rem", sm: "1rem", md: "1.2rem" }, fontWeight: "bold" }} />
                  )}
                </IconButton>
              ) : (
                <Box
                  sx={{
                    flexShrink: 0,
                    width: { xs: 12, sm: 13, md: 15 },
                    height: { xs: 12, sm: 13, md: 15 },
                  }}
                />
              )
            ) : !isSKU ? (
              row.category !== "Total" ? (
                <IconButton
                  size="small"
                  onClick={() =>
                    handleNestedRowToggle(parentCategory, row.category, idx)
                  }
                  sx={{ 
                    padding: { xs: 0.25, sm: 0.4, md: 0.5 },
                    color: "#1976d2",
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                      color: "#1565c0",
                      transform: "scale(1.1)"
                    },
                    transition: "all 0.2s ease-in-out"
                  }}
                >
                  {expandedNestedRows.has(`${parentCategory}-${idx}`) ? (
                    <ExpandLess sx={{ fontSize: { xs: "0.9rem", sm: "1rem", md: "1.2rem" }, fontWeight: "bold" }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: { xs: "0.9rem", sm: "1rem", md: "1.2rem" }, fontWeight: "bold" }} />
                  )}
                </IconButton>
              ) : (
                <Box
                  sx={{
                    flexShrink: 0,
                    width: { xs: 12, sm: 13, md: 15 },
                    height: { xs: 12, sm: 13, md: 15 },
                  }}
                />
              )
            ) : (
              <Box
                sx={{
                  flexShrink: 0,
                  width: { xs: 12, sm: 13, md: 15 },
                  height: { xs: 12, sm: 13, md: 15 },
                }}
              />
            )}

            <Typography
              variant="body2"
              sx={{
                flex: 1,
                minWidth: 0,
                fontWeight: isTotalRow ? 700 : undefined,
                fontSize: { 
                  xs: isSKU ? "0.5rem" : isNested ? "0.525rem" : "0.55rem",
                  sm: isSKU ? "0.6rem" : isNested ? "0.625rem" : "0.65rem",
                  md: isSKU ? "0.65rem" : isNested ? "0.7rem" : "0.75rem"
                },
                wordBreak: "break-word",
                whiteSpace: "normal",
                lineHeight: 1.2,
              }}
            >
              {row.category}
            </Typography>
          </Box>
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            minWidth: { xs: 50, sm: 65, md: 80 },
            borderRight: "1px solid #e0e0e0",
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
        >
          <ProgressBar
            value={row.valueSales}
            maxValue={maxValueForRow}
            isValueSales={true}
          />
        </TableCell>
        
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            fontWeight: "medium",
            borderRight: "1px solid #e0e0e0",
            color: getValueColor(row.rawMarketValueSalesIYA),
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
        >
          {row.marketValueSalesIYA}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            fontWeight: "medium",
            borderRight: "1px solid #e0e0e0",
            color: getValueColor(row.rawVolumeIYA),
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
        >
          {row.volumeIYA}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            fontWeight: "medium",
            borderRight: "1px solid #e0e0e0",
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
        >
          {row.avgPriceVol}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            fontWeight: "medium",
            borderRight: "2px solid #d4d4d4",
            color: getValueColor(row.rawAvgPriceVolIYA),
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
        >
          {row.avgPriceVolIYA}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            borderRight: "1px solid #e0e0e0",
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
        >
          <ProgressBar
            value={row.mfcValueSales}
            maxValue={maxMfcValueForRow}
            isValueSales={true}
          />
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            fontWeight: "medium",
            borderRight: "1px solid #ddd",
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
        >
          {row.mfcMarketShare < 0 || row.mfcMarketShare === undefined ? "N/A" : `${row.mfcMarketShare.toFixed(1)}%`}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            fontWeight: "medium",
            borderRight: "1px solid #ddd",
            color: getValueColor(row.rawMfcValueSalesIYA),
            backgroundColor: getComparisonBackground(row, 'rawMfcValueSalesIYA', 'rawMarketValueSalesIYA'),
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
        >
          {row.mfcValueSalesIYA}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            fontWeight: "medium",
            borderRight: "1px solid #ddd",
            color: getValueColor(row.rawMfcVolumeIYA),
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
        >
          {row.mfcVolumeIYA}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            fontWeight: "medium",
            borderRight: "1px solid #ddd",
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
        >
          {row.mfcAvgPriceVol}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
            fontWeight: "medium",
            color: getValueColor(row.rawMfcAvgPriceVolIYA),
            py: { xs: 0.75, sm: 1, md: 1.25 },
            px: { xs: 0.5, sm: 1, md: 1.5 },
          }}
        >
          {row.mfcAvgPriceVolIYA}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 0.5, sm: 1, md: 2 },
        backgroundColor: "transparent",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
        maxWidth: { xs: "100%", sm: "90%", md: "100%" },
        boxSizing: "border-box",
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: {
            xs: "0.9rem",
            sm: "1.2rem",
            md: "1.3rem",
          },
          px: {
            xs: 0.5,
            sm: 0.75,
            md: 1,
          },
          color: theme.palette.primary.main,
          textAlign: "left",
          m: 0,
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          height: 48,
        }}
      >
        Market vs Manufacturer Performance
      </Typography>
      <Box
        display="flex"
        justifyContent="center"
        mb={{ xs: 1, sm: 1.5, md: 2 }}
        px={{ xs: 1, sm: 0 }}
      >
        <CustomSelectBox
          sx={{
            fontWeight: 500,
            fontSize: {
              xs: "14px",
              sm: "16px",
              md: "18px",
            },
            height: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            minHeight: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            maxHeight: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            p: 0,
            display: "flex",
            alignItems: "center",
          }}
          value={hierarchyOptions.includes(selectedHierarchy) ? selectedHierarchy : ""}
          onChange={(event) => {
            console.log(
              "[MarketKPIsTable] CustomSelectBox changed to:",
              event.target.value
            );
            handleHierarchyChange(event.target.value);
          }}
          options={hierarchyOptions}
        />
      </Box>
      {loading || isRefreshing ? (
        <FacebookCircularProgress />
      ) : error ? (
        <Box
          sx={{
            width: "100%",
            minHeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
          }}
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message={`${error} - Click to reset table`}
          />
        </Box>
      ) : marketData.length === 0 ? (
        <Box
          sx={{
            width: "100%",
            minHeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
          }}
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message="No data available - Click to reset table"
          />
        </Box>
      ) : (
        <>
          {/* Main Table */}
          <Box
            sx={{
              width: "100%",
              "@media (max-width: 1099px)": {
                overflowX: "scroll",
                width: "100%",
                maxWidth: "100vw",
              },
              "@media (max-width: 600px)": {
                overflowX: "scroll",
                width: "95%",
                maxWidth: "80vw",
              },
            }}
          >
            <TableContainer
            sx={{
              width: "100%",
              overflowX: "hidden", // Hide horizontal scroll by default (for screens >= 1100px)
              overflowY: "auto",
              maxHeight: {
                xs: "calc(100vh - 200px)",
                sm: "calc(100vh - 180px)",
                md: "none",
              },
              // Show horizontal scroll for screens less than 1100px
              "@media (max-width: 1099px)": {
                overflowX: "scroll !important", // Force horizontal scroll on screens < 1100px
                width: "100%",
                maxWidth: "100vw",
                "&::-webkit-scrollbar": {
                  height: 10,
                  width: 10,
                  display: "block !important",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                  borderRadius: "5px",
                  border: "1px solid #ddd",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#666",
                  borderRadius: "5px",
                  border: "1px solid #444",
                  "&:hover": {
                    backgroundColor: "#333",
                  },
                },
                "&::-webkit-scrollbar-corner": {
                  backgroundColor: "#f1f1f1",
                },
              },
              // Enhanced horizontal scroll for small screens
              "@media (max-width: 600px)": {
                overflowX: "scroll !important", // Force scroll on small screens
                width: "100%",
                maxWidth: "100vw",
                "&::-webkit-scrollbar": {
                  height: 10,
                  width: 10,
                  display: "block !important",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                  borderRadius: "5px",
                  border: "1px solid #ddd",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#666",
                  borderRadius: "5px",
                  border: "1px solid #444",
                  "&:hover": {
                    backgroundColor: "#333",
                  },
                },
                "&::-webkit-scrollbar-corner": {
                  backgroundColor: "#f1f1f1",
                },
              },
            }}
          >
            <Table
              size="small"
              sx={{
                minWidth: { xs: 500, sm: 600, md: 800 },
                tableLayout: "fixed", // Changed from "auto" to "fixed" for better small screen control
                borderCollapse: "separate",
                borderSpacing: 0,
                // Ensure table doesn't shrink below minimum width on screens < 1100px
                "@media (max-width: 1099px)": {
                  minWidth: 1200, // Increased to ensure overflow on screens < 1100px
                  width: "max-content !important",
                  maxWidth: "none",
                },
                "@media (max-width: 600px)": {
                  minWidth: 800, // Increased to ensure overflow
                  width: "max-content !important",
                  maxWidth: "none",
                },
                "& th": {
                  whiteSpace: "normal", // Changed back to "normal" to allow text wrapping
                  wordBreak: "break-word", // Changed to "break-word" to allow breaking long words
                  maxWidth: { xs: 70, sm: 100, md: 120 },
                  minWidth: { xs: 60, sm: 80, md: 100 }, // Added minWidth to prevent columns from getting too narrow
                  fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                  px: { xs: 0.5, sm: 1, md: 1.5 },
                  py: { xs: 0.5, sm: 0.75, md: 1.25 },
                  borderBottom: "2px solid #e0e0e0",
                  lineHeight: 1.2, // Added line height for better text wrapping
                  // Enhanced header styling for small screens
                  "@media (max-width: 600px)": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflow: "visible", // Changed from "hidden" to "visible"
                    textOverflow: "unset", // Changed from "ellipsis" to "unset"
                    lineHeight: 1.1,
                  },
                },
                "& td": {
                  whiteSpace: "nowrap",
                  fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                  px: { xs: 0.5, sm: 1, md: 1.5 },
                  py: { xs: 0.5, sm: 0.75, md: 1 },
                  borderBottom: "1px solid #f0f0f0",
                },
                "& tr:hover": {
                  backgroundColor: "#fafafa",
                },
              }}
            >
              <colgroup>
                <col style={{ width: `${brandColumnWidth}px` }} />
                {Array.from({ length: 11 }).map((_, idx) => (
                  <col key={`metric-col-${idx}`} />
                ))}
              </colgroup>
              <TableHead>
                {/* Section Header Row */}
                <TableRow>
                  <TableCell
                    sx={{
                      border: "none",
                      backgroundColor: "transparent",
                      borderBottom: "2px solid #e0e0e0",
                    }}
                  />
                  <TableCell
                    align="center"
                    colSpan={5}
                    sx={{
                      fontWeight: 600,
                      fontSize: {
                        xs: "0.7rem !important",
                        sm: "0.9rem !important",
                        md: "1rem !important",
                      },
                      minWidth: { xs: 50, sm: 70, md: 80 },
                      backgroundColor: "#8acaec",
                      borderRight: "2px solid #d4d4d4",
                      borderBottom: "2px solid #e0e0e0",
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      px: { xs: 0.5, sm: 1, md: 1.5 },
                      color: "#1a1a1a",
                    }}
                  >
                    Market
                  </TableCell>
                  <TableCell
                    align="center"
                    colSpan={6}
                    sx={{
                      fontWeight: "bold",
                      fontSize: {
                        xs: "0.65rem",
                        sm: "0.75rem",
                        md: "0.875rem",
                      },
                      minWidth: { xs: 50, sm: 70, md: 80 },
                      backgroundColor: "#53b5ff",
                      borderBottom: "2px solid #e0e0e0",
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      px: { xs: 0.5, sm: 1, md: 1.5 },
                      color: "#1a1a1a",
                    }}
                  >
                    <ManufacturerSelector
                      selectedManufacturer={selectedManufacturer}
                      setSelectedManufacturer={setSelectedManufacturer}
                      manufacturerOptions={manufacturerOptions}
                    />
                  </TableCell>
                </TableRow>

                {/* Column Headers Row */}
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: brandColumnWidth,
                      maxWidth: brandColumnWidth + 40,
                      width: brandColumnWidth,
                      borderRight: "1px solid #ddd",
                      borderBottom: "2px solid #e0e0e0",
                      py: { xs: 0.75, sm: 1, md: 1.25 },
                      px: { xs: 0.5, sm: 1, md: 1.5 },
                    }}
                  >
                    {selectedHierarchy}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: { xs: 50, sm: 65, md: 80 },
                      borderRight: "1px solid #ddd",
                      px: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    Value Sales (M)
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: { xs: 45, sm: 60, md: 80 },
                      borderRight: "1px solid #ddd",
                      px: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    Value Sales IYA
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: { xs: 45, sm: 60, md: 80 },
                      borderRight: "1px solid #ddd",
                      px: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    Volume IYA
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: { xs: 45, sm: 60, md: 80 },
                      borderRight: "1px solid #ddd",
                      px: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    Avg Price /Vol (SAR)
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: { xs: 45, sm: 60, md: 80 },
                      borderRight: "2px solid #d4d4d4",
                      px: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    Avg Price /Vol IYA
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: { xs: 50, sm: 65, md: 80 },
                      borderRight: "1px solid #ddd",
                      px: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    Value Sales (M)
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: { xs: 45, sm: 60, md: 80 },
                      borderRight: "1px solid #ddd",
                      px: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    Value Share (%)
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: { xs: 45, sm: 60, md: 80 },
                      borderRight: "1px solid #ddd",
                      px: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    Value Sales IYA
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: { xs: 45, sm: 60, md: 80 },
                      borderRight: "1px solid #ddd",
                      px: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    Volume IYA
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: { xs: 45, sm: 60, md: 80 },
                      borderRight: "1px solid #ddd",
                      px: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    Avg Price /Vol (SAR)
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                      minWidth: { xs: 45, sm: 60, md: 80 },
                      px: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    Avg Price /Vol IYA
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {marketData.map((row, idx) => (
                  <React.Fragment key={idx}>
                    {renderTableRow(row, idx)}
                    {/* Render nested rows */}
                    {expandedRows.has(idx) &&
                      (loadingNested && !nestedData[row.category] ? (
                        <TableRow>
                          <TableCell colSpan={12} align="center" sx={{ py: 2 }}>
                            <FacebookCircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : (
                        nestedData[row.category] &&
                        nestedData[row.category].map((nestedRow, nestedIdx) => (
                          <React.Fragment
                            key={`nested-${row.category}-${nestedIdx}`}
                          >
                            {renderTableRow(
                              nestedRow,
                              nestedIdx,
                              true,
                              row.category
                            )}
                            {/* Render SKU level rows */}
                            {expandedNestedRows.has(
                              `${row.category}-${nestedIdx}`
                            ) &&
                              (loadingSKU ||
                              !nestedNestedData[
                                `${row.category}-${nestedIdx}`
                              ] ? (
                                <TableRow>
                                  <TableCell
                                    colSpan={12}
                                    align="center"
                                    sx={{ py: 2 }}
                                  >
                                    <FacebookCircularProgress />
                                  </TableCell>
                                </TableRow>
                              ) : (
                                nestedNestedData[
                                  `${row.category}-${nestedIdx}`
                                ].map((skuRow, skuIdx) =>
                                  renderTableRow(
                                    skuRow,
                                    skuIdx,
                                    true,
                                    `${row.category}-${nestedIdx}`,
                                    true
                                  )
                                )
                              ))}
                          </React.Fragment>
                        ))
                      ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

MarketKPIsTable.chartInfo = "IYA (Index Year Ago) Comparison vs. the same period last year";

export default MarketKPIsTable;